import { notification } from 'antd';
import { useAuth } from 'hooks/useAuth';
import qs from 'query-string';
import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { tfaHasBeenEnabled } from 'components/Login/tfaConstants';
import refreshExpiredToken from './refreshToken';
import { useDispatch } from 'react-redux';
import { setNeedPassCaptcha } from 'actions/rateLimits';

/**
 * Configuration object for useXHR hook
 * @typedef {Object} UseXHRConfiguration
 * @property {Function} onRequestError - Callback function to be called when the request errors
 * @property {Function} onRequestSuccess - Callback function to be called when the request succeeds
 * @property {Function} onRequestBegan - Callback function to be called when the request begins
 */

const states = {
  UNSENT: 0,
  OPENED: 1,
  HEADERS_RECEIVED: 2,
  LOADING: 3,
  DONE: 4,
};

/**
 * Custom XMLHttpRequest hook
 * @param {boolean} onMount
 * @param {UseXHRConfiguration | null} configuration
 * @returns Hook state and methods
 */
const useXHR = (onMount, configuration = null) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const auth = useAuth();
  const { pathname, search } = useLocation();
  const [state, setState] = useState({
    loading: onMount,
    res: null,
    error: null,
  });
  const isRetry = useRef(false);
  const isMounted = useRef(true);
  const req = useMemo(() => new XMLHttpRequest(), []);

  const handleRequestStart = () => {
    if (!isMounted.current) return;
    setState({ res: null, loading: true, error: null });
    configuration?.onRequestBegan?.();
  };

  const handleError = ({ status, message, statusText }) => {
    let parsedMessage;
    try {
      parsedMessage = message ? JSON.parse(message) : null;
    } catch (e) {
      parsedMessage = message || null;
    }
    // Preserve the raw parsed body (when it was valid JSON/an object) on `error.data`
    // so callers can inspect ProblemDetails fields like `title`, `type`, and
    // `errorCodes` without having to re-parse. Non-object payloads stay null.
    const rawData =
      parsedMessage && typeof parsedMessage === 'object' ? parsedMessage : null;
    let errors = parsedMessage?.errors ?? {};
    parsedMessage = parsedMessage?.responseStatus?.message
      ? parsedMessage.responseStatus.message
      : parsedMessage?.title ?? parsedMessage;
    if (!isMounted.current) return;
    setState(s => ({
      ...s,
      loading: false,
      error: {
        status,
        statusText,
        message: parsedMessage,
        errors,
        data: rawData,
      },
    }));
    configuration?.onRequestError?.({ status, message, statusText });
  };

  const handleRequestDone = async () => {
    if (!isMounted.current) return;
    if (req.status >= 200 && req.status < 300) {
      isRetry.current = false;
      let res;
      if (req.response) {
        try {
          res = JSON.parse(req.response);
        } catch (e) {
          res = { status: 'OK', data: req.response, req };
        }
      } else {
        res = { status: 'OK' };
      }

      if (res === 'OK') {
        res = { status: 'OK' };
      }

      // if (res && typeof res === 'object' && !Array.isArray(res)) {
      //   res._uuid = Date.now();
      // }
      setState({
        loading: false,
        res,
        error: null,
      });
      configuration?.onRequestSuccess?.(res);
    } else if (req.status === 429) {
      dispatch(setNeedPassCaptcha(true));
      return;
    } else if (req.status === 401 && !pathname.includes('/login')) {
      if (!isRetry.current && originalRequest.current) {
        isRetry.current = true;
        try {
          const { token, user } = await refreshExpiredToken();
          if (!isMounted.current) return;
          auth.updateUser(user);

          const retryReq = new XMLHttpRequest();

          const retryStateHandler = () => {
            switch (retryReq.readyState) {
              case states.OPENED:
                handleRequestStart();
                break;
              case states.DONE:
                // Clean up listeners after request is done
                retryReq.removeEventListener(
                  'readystatechange',
                  retryStateHandler
                );
                retryReq.removeEventListener('error', handleError);

                if (retryReq.status >= 200 && retryReq.status < 300) {
                  isRetry.current = false;
                  let res;
                  try {
                    res = JSON.parse(retryReq.response);
                  } catch (e) {
                    res = { status: 'OK', data: retryReq.response };
                  }
                  setState({
                    loading: false,
                    res,
                    error: null,
                  });
                  configuration?.onRequestSuccess?.(res);
                } else {
                  handleError({
                    status: retryReq.status,
                    message: retryReq.response,
                    statusText: retryReq.statusText,
                  });
                }
                break;
              default:
                break;
            }
          };

          retryReq.addEventListener('readystatechange', retryStateHandler);
          retryReq.addEventListener('error', handleError);

          retryReq.open(
            originalRequest.current.method,
            originalRequest.current.url
          );
          if (originalRequest.current.responseType) {
            retryReq.responseType = originalRequest.current.responseType;
          }

          const headers = originalRequest.current.headers || [];
          headers.forEach(header => {
            retryReq.setRequestHeader(header.key, header.value);
          });

          retryReq.setRequestHeader('Authorization', `Bearer ${token}`);
          retryReq.send(originalRequest.current.body);
          return;
        } catch (err) {
          isRetry.current = false;
          if (err?.tfaEnabled) {
            notification.warning({
              message: tfaHasBeenEnabled,
              placement: 'bottomLeft',
            });
          }
        }
      }

      let res;
      try {
        res = JSON.parse(req.response);
      } catch {}

      handleError({
        status: req.status,
        message: req.response,
        statusText: req.statusText,
      });

      // to avoid multiple redirects,
      // if user is authed we can clear user data and redirect will be handled by AuthedRoute, logout by login component
      if (auth?.isAuthed) {
        auth.softLogOut();
      } else {
        const parsedQueryParams = qs.parse(search);
        const queryParams = { redirect: pathname, ...parsedQueryParams };
        if (pathname === '/') {
          delete queryParams.redirect;
        }

        const redirectString = qs.stringify(queryParams, { encode: false });

        const redirectPath = `/login?${redirectString}`;
        push(redirectPath);
      }

      if (res?.tfaEnabled) {
        notification.warning({
          message: tfaHasBeenEnabled,
          placement: 'bottomLeft',
        });
      }
    } else if (req.status === 404) {
      const parsedQueryParams = qs.parse(search);
      const queryParams = { from: pathname, ...parsedQueryParams };
      if (pathname === '/') {
        delete queryParams.from;
      }
      const redirectString = qs.stringify(queryParams, { encode: false });
      handleError({
        status: req.status,
        message: req.response,
        statusText: req.statusText,
      });
      push(`/notfound?${redirectString}`);
    } else {
      if (isCanceled.current) {
        isCanceled.current = false;
        return;
      }

      handleError({
        status: req.status,
        message: req.response,
        statusText: req.statusText,
      });
    }
  };

  const handleReadyStateChange = () => {
    switch (req.readyState) {
      case states.OPENED:
        handleRequestStart();
        break;
      case states.DONE:
        handleRequestDone();
        break;
      default:
        break;
    }
  };

  const isCanceled = useRef(false);
  const cancel = useCallback(() => {
    isCanceled.current = true;
    req?.abort();
  }, [req]);

  const originalRequest = useRef(null);
  const setOriginalRequest = useCallback(
    value => (originalRequest.current = value),
    []
  );
  const setRes = value => {
    if (!isMounted.current) return;
    setState(s => ({ ...s, res: value }));
  };
  const setErr = value => {
    if (!isMounted.current) return;
    setState(s => ({ ...s, error: value }));
  };

  useEffect(() => {
    req.addEventListener('readystatechange', handleReadyStateChange);
    req.addEventListener('error', handleError);

    return () => {
      req.removeEventListener('readystatechange', handleReadyStateChange);
      req.removeEventListener('error', handleError);
    };
  });

  // When the calling component unmounts, this will be invoked.
  // Subsequent callbacks/setState calls check this and don't trigger
  // if it's false. The body must re-arm the flag: Fast Refresh re-runs
  // effects on the same instance (refs preserved), so cleanup alone
  // would leave the hook permanently deaf to responses after a hot edit.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return [state, req, setRes, setErr, setOriginalRequest, cancel];
};

export default useXHR;
